import { useCallback, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchCareEntitlements,
  fetchCaseDelay,
  fetchCaseSubCases,
  fetchDisruptionDelays,
  loadCareEntitlement,
  postDelayDeclaration,
  postDelayRelease,
  postGraceRelease,
  postMealIssue,
  postOutOfPolicyDismiss,
  type DelayRequestError,
} from './api';
import { createSubmissionKeys, type ISubmissionKeys } from './declaration';
import { careEntitlementsKey, caseDelayKey, caseSubCasesKey, disruptionDelaysKey } from './keys';
import { applyDelaySnapshot } from './merge';
import type { SubCase } from '@/lib/sub-cases/types';
import type {
  ICareEntitlementView,
  ICaseFlightDelay,
  IDelayDeclarationBody,
  IDelayDeclarationResult,
  IDelayReleaseBody,
  IDelayReleaseResult,
  ILoadCareEntitlementResult,
  IMealIssueBody,
  IMealIssueResult,
  IOutOfPolicyDismissBody,
} from './types';

// Flight-delay data hooks (U1), the app's TanStack Query conventions: ONE HTTP read
// seeds each cache, then the WS owns freshness — `FlightDelayChanged` /
// `FlightEscalated` → `applyEvent` → `lib/delay/merge.ts` (setQueryData). Hence
// `staleTime: Infinity` and no refetch interval anywhere (frontend-conventions §8.1).
// A socket reconnect resyncs every cached read once (`lib/ws/socket.ts`). The pages
// that mount these hooks already sit in the disruption's room (`joinDisruption`).

/** The disruption's delay timelines (header badge + flight picker). */
export function useDisruptionDelays(disruptionUrn: string | undefined, enabled = true) {
  return useQuery<ICaseFlightDelay[]>({
    queryKey: disruptionDelaysKey(disruptionUrn ?? ''),
    queryFn: () => fetchDisruptionDelays(disruptionUrn as string),
    enabled: !!disruptionUrn && enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** One case's full timeline (history included) — `null` when the case has none. */
export function useCaseDelay(caseUrn: string | undefined, enabled = true) {
  return useQuery<ICaseFlightDelay | null>({
    queryKey: caseDelayKey(caseUrn ?? ''),
    queryFn: () => fetchCaseDelay(caseUrn as string),
    enabled: !!caseUrn && enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** The case's delay-care entitlements (wallet). The BFF gates the route to AIRPORT_OPERATOR+. */
export function useCareEntitlements(caseUrn: string | undefined, enabled = true) {
  return useQuery<ICareEntitlementView[]>({
    queryKey: careEntitlementsKey(caseUrn ?? ''),
    queryFn: () => fetchCareEntitlements(caseUrn as string),
    enabled: !!caseUrn && enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** Load a PENDING / FAILED entitlement; the wallet's answer is folded straight into the list. */
export function useLoadCareEntitlement(caseUrn: string) {
  const qc = useQueryClient();
  return useMutation<ILoadCareEntitlementResult, DelayRequestError, string>({
    mutationFn: (entitlementKey) => loadCareEntitlement(entitlementKey),
    onSuccess: ({ entitlement }) => {
      qc.setQueryData<ICareEntitlementView[]>(careEntitlementsKey(caseUrn), (rows) =>
        rows?.map((r) => (r.entitlementKey === entitlement.entitlementKey ? entitlement : r)),
      );
    },
  });
}

interface IDeclareArgs {
  body: IDelayDeclarationBody;
  key: string;
}

/**
 * Declare by hand. `submit(body)` sends it under the SUBMISSION's Idempotency-Key
 * (`createSubmissionKeys`): the same key when the operator retries the same body after
 * a failure, a fresh one once it landed or when the body changed. The answer's timeline
 * is folded into every delay cache at once (the WS echo then lands as a no-op).
 */
export function useDeclareDelay(disruptionUrn?: string) {
  const qc = useQueryClient();
  const keys = useRef<ISubmissionKeys | null>(null);
  const mutation = useMutation<IDelayDeclarationResult, DelayRequestError, IDeclareArgs>({
    mutationFn: ({ body, key }) => postDelayDeclaration(body, key),
    onSuccess: (result) => {
      keys.current?.settle();
      applyDelaySnapshot(qc, result.delay, result.disruptionUrn ?? disruptionUrn);
    },
  });
  const { mutateAsync } = mutation;
  const submit = useCallback(
    (body: IDelayDeclarationBody): Promise<IDelayDeclarationResult> => {
      keys.current ??= createSubmissionKeys();
      return mutateAsync({ body, key: keys.current.keyFor(body) });
    },
    [mutateAsync],
  );
  return { ...mutation, submit };
}

/**
 * The case's PNR groups, projected by `select` (the URN → locator labels of a release
 * result). Same key and route as `useSubCasesForCase`, so both share one cache entry;
 * read only while `enabled` (a result is shown) — never polled.
 */
export function useCaseGroups<T>(
  caseUrn: string,
  enabled: boolean,
  select: (rows: SubCase[]) => T,
) {
  return useQuery<SubCase[], Error, T>({
    queryKey: caseSubCasesKey(caseUrn),
    queryFn: () => fetchCaseSubCases(caseUrn),
    enabled: !!caseUrn && enabled,
    staleTime: 10_000,
    refetchOnWindowFocus: false,
    select,
  });
}

/**
 * Release the case's delay hold to Nexa's rebooking (OPS_SUPERVISOR / ADMIN — the BFF
 * enforces it, the panel hides the action below it). The answer's timeline (hold no
 * longer active, `releasedBy` set) is folded into every delay cache at once, and the
 * case's groups are marked stale — the released ones left the delay stamp. The rebooking
 * run then reports through its own WS events.
 */
export function useReleaseDelay(caseUrn: string, disruptionUrn?: string) {
  const qc = useQueryClient();
  return useMutation<IDelayReleaseResult, DelayRequestError, IDelayReleaseBody>({
    mutationFn: (body) => postDelayRelease(caseUrn, body),
    onSuccess: (result) => {
      if (result.delay) applyDelaySnapshot(qc, result.delay, result.disruptionUrn ?? disruptionUrn);
      void qc.invalidateQueries({ queryKey: caseSubCasesKey(caseUrn), exact: true });
    },
  });
}

/**
 * «Dar la comida ya» (meal timing, 2026-10-30). The answer's timeline (the tiers issued, the
 * next meal gone) is folded into every delay cache at once and the case's entitlements are
 * re-read (the wallet records the new tiers); the `FlightDelayChanged` echo then lands as a no-op.
 */
export function useIssueMealsNow(caseUrn: string, disruptionUrn?: string) {
  const qc = useQueryClient();
  return useMutation<IMealIssueResult, DelayRequestError, IMealIssueBody>({
    mutationFn: (body) => postMealIssue(caseUrn, body),
    onSuccess: (result) => {
      if (result.delay) applyDelaySnapshot(qc, result.delay, result.disruptionUrn ?? disruptionUrn);
      void qc.invalidateQueries({ queryKey: careEntitlementsKey(caseUrn), exact: true });
    },
  });
}

/**
 * «Pasar ya a Nexa» for every escalated case of the disruption — one early grace release
 * per case, in turn. The timelines then move to ESCALATED_RELEASED (the WS echo merges
 * them); the delays read is re-read too so the screen leaves the window at once.
 */
export function useReleaseGraceNow(disruptionUrn: string) {
  const qc = useQueryClient();
  return useMutation<void, DelayRequestError, readonly string[]>({
    mutationFn: async (caseUrns) => {
      for (const caseUrn of caseUrns) await postGraceRelease(caseUrn);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: disruptionDelaysKey(disruptionUrn) });
    },
  });
}

/**
 * «Descartar» an out-of-policy provider report (delay-limits). The answer's timeline (flag
 * cleared) is folded into every delay cache and the rows' `flightStatus` at once; the
 * `FlightDelayChanged` echo then lands as a no-op.
 */
export function useDismissOutOfPolicy(caseUrn: string, disruptionUrn?: string) {
  const qc = useQueryClient();
  return useMutation<ICaseFlightDelay, DelayRequestError, IOutOfPolicyDismissBody>({
    mutationFn: (body) => postOutOfPolicyDismiss(caseUrn, body),
    onSuccess: (delay) => applyDelaySnapshot(qc, delay, disruptionUrn),
  });
}
